import React from 'react';
import { Link } from 'react-router-dom';
import Icon from '@mdi/react';
import {
    mdiClipboardTextOutline,
    mdiCheckCircle,
    mdiCog,
} from '@mdi/js';
import BaseModal from './BaseModal';
import './OnboardingChecklistModal.css';

/**
 * The three status states, defined once so the step badges and the legend that
 * explains them are rendered from the same source and cannot drift apart.
 *
 * Three states, not two: green tick = the org completed it; grey tick = a pre-filled
 * card that needs no action (the legend and its "(Defaults Set)" tag say so); amber
 * cog = genuinely still outstanding. The two settled states share the solid tick and
 * differ by colour; the outstanding state uses a different glyph on purpose, because
 * a tick reads as success no matter what colour it is, and an amber tick would say
 * "done, but a problem", which is not what this state means. The cog says "needs
 * setting up" and matches where the step sends you.
 */
const STEP_STATES = {
    complete: {
        path: mdiCheckCircle,
        color: 'var(--dl-color-green-500)',
        className: '',
        legend: 'Done',
    },
    prefilled: {
        path: mdiCheckCircle,
        color: 'var(--dl-color-gray-500)',
        className: '',
        legend: 'Defaults set',
    },
    incomplete: {
        path: mdiCog,
        color: 'var(--dl-color-gray-white)',
        className: ' onboarding-checklist-step-check-todo',
        legend: 'To do',
    },
};

const LEGEND_ORDER = ['complete', 'prefilled', 'incomplete'];

/**
 * The status glyph. On a step card it is absolutely positioned to straddle the card's
 * top-right corner; in the legend `inline` drops that positioning so it sits in the
 * text flow, while glyph, colour, disc and ring stay identical.
 *
 * No hover tooltips: the legend above the list names all three states in words, which
 * reads on touch too. Note a `title` prop on <Icon> would not give one anyway -
 * @mdi/react renders it as an SVG <title> child, not an HTML title attribute.
 *
 * The icons are decorative given that legend, so they are hidden from screen readers
 * rather than repeating the state in every card's accessible name.
 */
const StatusBadge = ({ state, inline = false }) => {
    const config = STEP_STATES[state];
    if (!config) return null;

    return (
        <Icon
            path={config.path}
            size={0.85}
            color={config.color}
            className={`onboarding-checklist-step-check${config.className}${inline ? ' onboarding-checklist-step-check-inline' : ''}`}
            aria-hidden="true"
        />
    );
};

/**
 * OnboardingChecklistModal — "Complete your setup" launcher.
 *
 * Shown to a freshly-onboarded org so they know where to go next. The steps are
 * conditional on what the org does (Settings "What you do", set at registration), so a
 * school sees school + ticketing steps while an instructor-only org does not.
 *
 * Presentational only: the step list, the per-step state and the counts all come from
 * useOnboardingChecklist, which the dashboard calls once and shares with the icon
 * badge, so both always agree on what is outstanding.
 *
 * Each step is a deep-link that closes the modal and navigates to its setup page. The
 * user can re-open the list any time from the checklist icon beside the Settings cog,
 * so nothing is lost if they dismiss it.
 *
 * Props:
 *   isOpen          {boolean}
 *   onClose         {function}
 *   groups          {Array}   gated groups, each step carrying its `state`
 *   hasStatus       {boolean} status resolved; false => render no states or bar
 *   totalCount      {number}
 *   completeCount   {number}
 */
const OnboardingChecklistModal = ({
    isOpen,
    onClose,
    groups,
    hasStatus,
    totalCount,
    completeCount,
}) => {
    // Nothing relevant to set up (e.g. a tools-free config) — render nothing.
    if (groups.length === 0) return null;

    // Plain click navigates in-tab via the <Link>, so we just close the modal. A
    // modifier-click (Cmd/Ctrl/middle/shift) opens the step in a NEW tab — leave the
    // modal open in that case so the user can keep working through the list.
    const handleStepClick = (e) => {
        const opensNewTab = e.metaKey || e.ctrlKey || e.shiftKey || e.button === 1;
        if (!opensNewTab) {
            onClose();
        }
    };

    const percentComplete = totalCount > 0 ? Math.round((completeCount / totalCount) * 100) : 0;
    const remainingCount = Math.max(totalCount - completeCount, 0);

    // The grey "defaults set" state only exists on the T&C steps, which are gated on the
    // Class Management / Private Lessons tools. A ticketing-only org sees no grey ticks at
    // all, so its legend must not explain an icon that appears nowhere on their list.
    // Badges render from the fetched `state`, so a prefilled step the org has actually
    // completed shows a green badge, not a grey one; the legend must follow the same rule.
    // `state` is null until the status loads, which still counts as grey for first paint.
    const hasPrefilledStep = groups.some(group =>
        group.steps.some(step => step.prefilled && step.state !== 'complete')
    );
    const legendStates = LEGEND_ORDER.filter(state => state !== 'prefilled' || hasPrefilledStep);

    return (
        <BaseModal
            isOpen={isOpen}
            onClose={onClose}
            title="Complete your setup checklist"
            icon={mdiClipboardTextOutline}
            footer={
                <button className="base-modal-cancel-btn" onClick={onClose}>
                    Close
                </button>
            }
        >
            <p className="onboarding-checklist-intro">
                Here are the steps to get your account ready. You do not have to do them all now,
                you can come back to this list any time from the checklist icon next to Settings.
            </p>

            {/* Progress bar. Hidden until the status resolves, so a slow or failed fetch
                never flashes an alarming empty bar. Pre-filled steps count as done here
                for the same reason they are excluded from the badge: they already work.
                That keeps this bar and the badge consistent (complete + outstanding = total).
                Header row (percentage left, count right, above the bar) mirrors the mobile
                ProfileCompletenessMeter so both platforms read the same way. */}
            {hasStatus && (
                <div className="onboarding-checklist-progress">
                    <div className="onboarding-checklist-progress-header">
                        <span className="onboarding-checklist-progress-percent">
                            Your setup: {percentComplete}%
                        </span>
                        <span className="onboarding-checklist-progress-count">
                            {completeCount} of {totalCount}
                        </span>
                    </div>
                    <div
                        className="onboarding-checklist-progress-track"
                        role="progressbar"
                        aria-valuenow={completeCount}
                        aria-valuemin={0}
                        aria-valuemax={totalCount}
                        aria-label="Setup progress"
                    >
                        <div
                            className="onboarding-checklist-progress-fill"
                            style={{ width: `${percentComplete}%` }}
                        />
                    </div>
                    <span className="onboarding-checklist-progress-label">
                        {remainingCount === 0
                            ? 'All steps complete'
                            : remainingCount === 1
                                ? '1 step left to finish your setup'
                                : `${remainingCount} steps left to finish your setup`}
                    </span>

                    {/* Legend for the per-step status badges. Sits under the count rather
                        than beside the list so it is read once, before the steps. Inside
                        the hasStatus block on purpose: when the status never resolves no
                        badges render, and a legend for absent icons would only confuse. */}
                    <ul className="onboarding-checklist-legend">
                        {legendStates.map(state => (
                            <li key={state} className="onboarding-checklist-legend-item">
                                <StatusBadge state={state} inline />
                                {STEP_STATES[state].legend}
                            </li>
                        ))}
                    </ul>
                </div>
            )}

            {groups.map(group => (
                <div key={group.key} className="onboarding-checklist-group">
                    <h3 className="onboarding-checklist-group-heading">{group.heading}</h3>
                    <div className="onboarding-checklist-steps">
                        {group.steps.map((step, index) => (
                            <Link
                                key={step.key}
                                to={step.route}
                                className="onboarding-checklist-step"
                                onClick={handleStepClick}
                                onAuxClick={handleStepClick}
                            >
                                {/* Status badge, pinned to the card's top-right corner,
                                    straddling the border. Independent of the inline number
                                    badge: the number answers "which step in sequence", this
                                    answers "where does it stand". Glyph, colour and meaning
                                    all come from STEP_STATES, shared with the legend above.
                                    Nothing renders until the status resolves. */}
                                {hasStatus && <StatusBadge state={step.state} />}
                                <span className="onboarding-checklist-step-header">
                                    <Icon path={step.icon} size={0.9} color="var(--dl-color-purple-default)" />
                                    <span className="onboarding-checklist-step-title">{step.title}</span>
                                    {/* Number only when the section has multiple steps to
                                        sequence; a lone item doesn't need a "1" badge. */}
                                    {group.steps.length > 1 && (
                                        <span className="onboarding-checklist-step-number">{index + 1}</span>
                                    )}
                                </span>
                                <span className="onboarding-checklist-step-desc">
                                    {step.description}
                                    {/* Says in words what the grey check says in colour: this
                                        card already works on platform defaults, so there is
                                        nothing outstanding here. Keyed off the static
                                        `prefilled` flag, not the fetched state, so it is
                                        there on first paint. */}
                                    {step.prefilled && (
                                        <strong className="onboarding-checklist-step-defaults"> (Defaults Set)</strong>
                                    )}
                                </span>
                            </Link>
                        ))}
                    </div>
                </div>
            ))}
        </BaseModal>
    );
};

export default OnboardingChecklistModal;
